import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { Plus, LogOut, Settings, Camera } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import UniversalInput from '@/components/UniversalInput';
import VoiceInput from '@/components/VoiceInput';
import InlineSearch from '@/components/InlineSearch';
import QueuePanel from '@/components/QueuePanel';
import { useQueue } from '@/components/QueueContext';
import { useState, useEffect } from 'react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useConfirmation } from './shared/ZCard';
import api from '@/lib/api';
import Logotype from '@/components/shared/Logotype';
import { SquircleDefs } from '@/components/shared/Logotype';

// ── Sync Claude modal ─────────────────────────────────────────────────────────
function SyncModal({ open, onClose, loading, result, onSelect, copied, onCopy }) {
  if (!open) return null;
  const PURPLE = '#6B35B8';
  const OPTIONS = [
    { type: 'context', label: 'Context Sync', sub: 'Codex, tasks, trips, assets — current state of the system' },
    { type: 'full',    label: 'Full Sync + Codebase', sub: 'Context plus all source files — use before architecture sessions' },
  ];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '28px 32px', width: 440, maxWidth: '90vw' }}>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: PURPLE, marginBottom: 20 }}>
          Sync Claude Context
        </p>
        {!result && !loading && OPTIONS.map(opt => (
          <button key={opt.type} onClick={() => onSelect(opt.type)}
            style={{ width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6, padding: '14px 16px', cursor: 'pointer', marginBottom: 10, display: 'block', transition: 'border-color 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = `${PURPLE}88`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
          >
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 400, color: '#f0f0f5', marginBottom: 5 }}>{opt.label}</p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 300, color: 'rgba(240,240,245,0.4)' }}>{opt.sub}</p>
          </button>
        ))}
        {loading && (
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.25)', textAlign: 'center', padding: '28px 0' }}>
            Building context…
          </p>
        )}
        {result && !loading && (
          <div>
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '0.13em', color: 'rgba(255,255,255,0.3)', marginBottom: 18 }}>
              {result.char_count.toLocaleString()} chars · {result.filename}
            </p>
            <button onClick={onCopy} style={{ width: '100%', padding: '12px', background: copied ? '#1A4532' : PURPLE, border: 'none', borderRadius: 5, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#fff', cursor: 'pointer', transition: 'background 0.3s ease' }}>
              {copied ? 'Copied' : 'Copy to Clipboard'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const navItems = [
  { path: '/uncode',   logo: '/logo-uncode.png',   label: 'Uncode',     shortLabel: 'Uncode',   tagline: 'Chaos into Clarity',        color: '#E85D24', appKey: 'uncode' },
  { path: '/giraffe',  logo: '/logo-giraffe.png',  label: 'Giraffe',    shortLabel: 'Giraffe',  tagline: 'The Journey Unraveled',     color: '#10B981', appKey: 'giraffe' },
  { path: '/verve',    logo: '/logo-verve.png',    label: 'Verve',      shortLabel: 'Verve',    tagline: 'Forging Freedom',           color: '#FF0042', appKey: 'verve' },
  { path: '/soulsync', logo: '/logo-soulsync.png', label: 'SoulSync',   shortLabel: 'Soul',     tagline: 'Architecting The Self',     color: '#4B5FD4', appKey: 'soulsync' },
  { path: '/dash',     logo: '/logo-dash.png',     label: 'Dash',       shortLabel: 'Dash',     tagline: '',                          color: '#06B6D4', appKey: 'dash' },
  { path: '/cult',     logo: '/logo-cult.png',     label: 'Cult of One',shortLabel: 'Cult',     tagline: 'Manifest of a Movement',    color: '#F5A623', appKey: 'cult' },
  { path: '/framed',   logo: '/logo-framed.png',   label: 'Framed',     shortLabel: 'Framed',   tagline: 'A Life in Technicolor',     color: '#D4537E', appKey: 'framed' },
  { path: '/arx',      logo: '/logo-arx.png',      label: 'Arx Acta',   shortLabel: 'Arx',      tagline: 'The Archive of Life',       color: '#C0A96A', appKey: 'arx' },
  { path: '/flux',     logo: '/logo-flux.png',     label: 'Flux',       shortLabel: 'Flux',     tagline: '',                          color: '#E24B4A', appKey: 'flux' },
  { path: '/xent',     logo: '/logo-xent.png',     label: 'xeNT',       shortLabel: 'xeNT',    tagline: 'Eclectically Eccentric',    color: '#ffffff', appKey: 'xent' },
  { path: '/zephyr',   logo: '/logo-zephyr.png',   label: 'Zephyr',     shortLabel: 'Z',        tagline: 'Awakening Interface',       color: '#6B35B8', isZephyr: true, appKey: 'zephyr' },
];

export default function Shell() {
  const feedback = useConfirmation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { count, openQueue } = useQueue();
  const [showInput, setShowInput] = useState(false);
  const [inputContext, setInputContext] = useState({ app: null, tripId: null });
  const [showVoice, setShowVoice] = useState(false);
  const [hideBottomNav, setHideBottomNav] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncCopied, setSyncCopied] = useState(false);
  const { isSupported, isSubscribed, subscribe, unsubscribe } = usePushNotifications();

  // Listen for sync-claude events from Dashboard mobile
  useEffect(() => {
    const handler = () => setSyncOpen(true);
    window.addEventListener('zephyr-sync-claude', handler);
    return () => window.removeEventListener('zephyr-sync-claude', handler);
  }, []);

  const handleSyncSelect = async (type) => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const r = await api.get(`/sync/${type}`);
      setSyncResult(r.data);
    } catch {
      feedback.fail('Sync failed');
      setSyncLoading(false);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncCopy = () => {
    if (!syncResult?.content) return;
    navigator.clipboard.writeText(syncResult.content).then(() => {
      setSyncCopied(true);
      setTimeout(() => setSyncCopied(false), 2000);
    });
  };

  const handleSyncClose = () => {
    setSyncOpen(false);
    setSyncLoading(false);
    setSyncResult(null);
    setSyncCopied(false);
  };

  const handleNotificationToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) feedback.confirm('Notifications disabled');
    } else {
      const success = await subscribe();
      if (success) {
        feedback.confirm('Notifications enabled - Sunday 9am Flow alerts');
      } else {
        feedback.fail('Could not enable notifications');
      }
    }
  };

  // Hide bottom nav when giraffe quick-add bar is focused
  useEffect(() => {
    const hide = () => setHideBottomNav(true);
    const show = () => setHideBottomNav(false);
    window.addEventListener('quick-add-focus', hide);
    window.addEventListener('quick-add-blur', show);
    return () => {
      window.removeEventListener('quick-add-focus', hide);
      window.removeEventListener('quick-add-blur', show);
    };
  }, []);

  // Snap viewport back to 1x after any input loses focus (iOS auto-zoom recovery)
  useEffect(() => {
    const snapBack = () => {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) return;
      const orig = viewport.getAttribute('content');
      viewport.setAttribute('content', orig + ', maximum-scale=1');
      requestAnimationFrame(() => viewport.setAttribute('content', orig));
    };
    document.addEventListener('focusout', snapBack);
    return () => document.removeEventListener('focusout', snapBack);
  }, []);

  // Cmd+K shortcut handled in InlineSearch component

  const getAppContext = () => {
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'giraffe' && parts[1]) {
      return { app: 'giraffe', tripId: parts[1], mode: 'entry' };
    }
    if (parts[0] === 'giraffe') {
      return { app: 'giraffe', tripId: null, mode: 'trip' };
    }
    if (parts[0] === 'uncode') {
      return { app: 'uncode', tripId: null, mode: 'entry' };
    }
    if (parts[0] === 'soulsync') {
      return { app: 'soulsync', tripId: null, mode: 'entry' };
    }
    return { app: null, tripId: null, mode: null };
  };

  const appContext = getAppContext();

  const handleFAB = () => {
    // Universal FAB — every app opens the same UniversalInput with the current
    // app as the classification hint. Voice, text, and file upload in one place.
    setInputContext({ app: appContext.app, tripId: appContext.tripId });
    setShowInput(true);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] pb-20 md:pb-0 md:pl-72">
      <SquircleDefs />
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-72 bg-[#0a0a0f] border-r border-white/[0.06] p-6 flex-col z-50">
        <button
          onClick={() => navigate('/dashboard')}
          className={`flex items-center mb-4 px-2 py-1.5 rounded-lg transition-all duration-300 -mx-2 ${
            location.pathname === '/dashboard' ? 'bg-[#6B35B8]/10' : 'hover:bg-white/[0.04]'
          }`}
          style={{ gap: 2 }}
        >
          <Logotype app="zephyr" size="dashboard" />
          <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 11, fontWeight: 500,
            letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6B35B8', lineHeight: 1, marginLeft: 4 }}>
            OS
          </span>
        </button>

        {/* Inline Search */}
        <InlineSearch />

        <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const hasQueueBadge = item.isZephyr && count.total > 0;
            const handleNavClick = () => {
              if (item.isZephyr && count.total > 0) {
                openQueue();
              } else {
                navigate(item.path);
              }
            };
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-300 text-left ${
                  active
                    ? 'bg-[#6B35B8]/10'
                    : 'hover:bg-white/[0.22]'
                }`}
              >
                <div className="flex-1 min-w-0" style={{ filter: active ? 'none' : 'brightness(0.7)' }}>
                  <Logotype app={item.appKey} size="sidebar" />
                </div>
                {hasQueueBadge && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#E8C547', flexShrink: 0 }}>
                    ● {count.total}
                  </span>
                )}
              </button>
            );
          })}

          <div className="my-2 border-t border-white/[0.06]" />

          <div className="flex items-center gap-1 px-2">
            {/* Logout */}
            <div className="relative group">
              <button
                data-testid="nav-logout"
                onClick={() => { logout(); navigate('/auth'); }}
                className="w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 text-[#b0b4c0] hover:text-[#f0f0f5] hover:bg-white/[0.03]"
              >
                <LogOut size={16} strokeWidth={1.5} />
              </button>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-white/[0.05] border border-white/[0.08] text-[#f0f0f5] text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                Logout
              </span>
            </div>
            {/* Admin */}
            <div className="relative group">
              <button
                data-testid="nav-admin"
                onClick={() => navigate('/admin/architecture')}
                className="w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 text-[#b0b4c0] hover:text-[#f0f0f5] hover:bg-white/[0.03]"
              >
                <Settings size={16} strokeWidth={1.5} />
              </button>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-white/[0.05] border border-white/[0.08] text-[#f0f0f5] text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                Admin
              </span>
            </div>
            {/* Gallery */}
            <div className="relative group">
              <a
                data-testid="nav-gallery"
                href={`${window.location.protocol}//${window.location.hostname}/gallery`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 text-[#b0b4c0] hover:text-[#f0f0f5] hover:bg-white/[0.03]"
              >
                <Camera size={16} strokeWidth={1.5} />
              </a>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-white/[0.05] border border-white/[0.08] text-[#f0f0f5] text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                Gallery
              </span>
            </div>
            {/* Sync Claude */}
            <div className="relative group">
              <button
                data-testid="nav-sync-claude"
                onClick={() => setSyncOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 text-[#b0b4c0] hover:text-[#6B35B8] hover:bg-white/[0.03]"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13.5 8a5.5 5.5 0 1 1-1.1-3.3"/>
                  <polyline points="11 2 13.5 4.5 11 7"/>
                </svg>
              </button>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-white/[0.05] border border-white/[0.08] text-[#f0f0f5] text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                Sync Claude
              </span>
            </div>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="min-h-screen">
        <Outlet />
      </main>

      {/* Desktop FAB — floating, desktop only */}
      <div className="hidden md:flex fixed bottom-12 right-8 items-center gap-3 z-40">
        <button
          data-testid="fab-universal-input"
          onClick={handleFAB}
          className="w-14 h-14 rounded-full bg-[#6B35B8] text-white flex items-center justify-center shadow-[0_0_30px_rgba(107,53,184,0.35)] hover:shadow-[0_0_45px_rgba(107,53,184,0.55)] transition-all duration-500 hover:scale-105 active:scale-95"
        >
          <Plus size={24} strokeWidth={2} />
        </button>
      </div>

      {showInput && <UniversalInput onClose={() => { setShowInput(false); setInputContext({ app: null, tripId: null }); }} initialApp={inputContext.app} initialTripId={inputContext.tripId} />}
      <QueuePanel />
      <SyncModal open={syncOpen} onClose={handleSyncClose} loading={syncLoading} result={syncResult} onSelect={handleSyncSelect} copied={syncCopied} onCopy={handleSyncCopy} />

      {/* Mobile Bottom Bar — Home + Voice + FAB */}
      <div className={`md:hidden fixed bottom-0 left-0 right-0 bg-[#0a0a0f]/90 backdrop-blur-xl border-t border-white/[0.06] flex items-center justify-between px-6 py-3 z-50 transition-transform duration-200 ${hideBottomNav ? 'translate-y-full' : 'translate-y-0'}`}>
        {/* Zephyr OS logo */}
        <button
          data-testid="mobile-nav-home"
          onClick={() => navigate('/dashboard')}
          className="flex items-center transition-all duration-300 active:scale-95"
          style={{ gap: 2 }}
        >
          <img
            src="/logo-zephyr.png"
            alt="Zephyr OS"
            style={{ width: 24, height: 24, clipPath: 'url(#squircle)', flexShrink: 0 }}
          />
          <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: 11, fontWeight: 300,
            letterSpacing: '0.18em', textTransform: 'uppercase', color: '#f0f0f5', lineHeight: 1 }}>
            Dashboard
          </span>
        </button>

        {/* FAB */}
        <button
          data-testid="fab-universal-input-mobile"
          onClick={handleFAB}
          className="w-12 h-12 rounded-full bg-[#6B35B8] text-white flex items-center justify-center shadow-[0_0_24px_rgba(107,53,184,0.35)] active:scale-95 transition-all duration-300"
        >
          <Plus size={22} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
